import { IconName } from '@fortawesome/fontawesome-svg-core';
import { CardControl } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/CardControl';
import { QuickExpand } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickExpand';
import { QuickSave } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickSave';
import { TextValuePreview } from 'components/detail/edit/TextValuePreview';
import { GradientLine } from 'components/detail/minute/GradientLine';
import { TextValue as TextValue2 } from 'elements/minute/sidebar/sidebarComponents/atomic/TextValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSectionHeader } from 'elements/minute/sidebar/sidebarSection/SidebarSection/SidebarSectionHeader';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { motion } from 'framer-motion';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EHovers } from 'libs/styles/styling';
import { useContext, useState } from 'react';
import { CtxDetail } from 'session/ctxDetail';
import styled, { css } from 'styled-components/macro';

interface Props {
    type: {
        iconName: IconName;
        fieldName: string;
        fieldSubName: string;
        title: string;
        subTitle: string;
    };
    position: number;
    className?: string;
}

export const EditSocialsCard = (props: Props): JSX.Element => {
    const ctxDetail = useContext(CtxDetail);
    const card = ctxDetail.updateDetailInput.cards[props.position];
    const [value, setValue] = useState(card[props.type.fieldName][props.type.fieldSubName]);
    const isUpdatingIndex = ctxDetail.updatingIndex === props.position;

    function changeValue(value: string): void {
        setValue(value);
        ctxDetail.updateCardByPosition(
            {
                [props.type.fieldName]: {
                    [props.type.fieldSubName]: value
                }
            },
            props.position
        );
    }

    return (
        <motion.div {...conditionalScaleAnimation(true, 1.02, 1.02)}>
            <S_MinuteCard
                className={props.className}
                $isUpdatingIndex={isUpdatingIndex}
                onClick={() => {
                    if (!isUpdatingIndex) {
                        ctxDetail.setUpdatingIndex(props.position);
                    }
                }}
            >
                <S_SidebarSectionHeader
                    $isUpdatingIndex={isUpdatingIndex}
                    icon={{
                        iconName: props.type.iconName,
                        iconSet: 'fab'
                    }}
                    title={props.type.title}
                    highlighted={!!isUpdatingIndex}
                    topBorder
                >
                    {isUpdatingIndex && <QuickSave />}
                    {!isUpdatingIndex && <QuickExpand />}
                </S_SidebarSectionHeader>
                {!isUpdatingIndex && (
                    <S_Content $isUpdatingIndex={isUpdatingIndex}>
                        <S_TextValuePreview value={value} />
                    </S_Content>
                )}
                {isUpdatingIndex && (
                    <>
                        <SidebarSubsection title={props.type.subTitle} icon={{ iconName: 'link' }}>
                            <SidebarContent>
                                <S_TextValue2
                                    value={value}
                                    onChange={changeValue}
                                    textAlign="left"
                                />
                            </SidebarContent>
                        </SidebarSubsection>

                        <CardControl position={props.position} />
                        <S_GradientLine />
                    </>
                )}
            </S_MinuteCard>
        </motion.div>
    );
};

const S_MinuteCard = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
    border-right: 1px solid #d3d3d3;
    border-left: 1px solid #d3d3d3;
    ${(props) => {
        if (props.$isUpdatingIndex) {
            return css`
                border-right: 1px solid #8f3a19;
                border-left: 1px solid #b19052;
            `;
        }
    }}
    ${(props) => {
        if (!props.$isUpdatingIndex) {
            return css`
                &:hover {
                    ${S_SidebarSectionHeader} {
                        box-shadow: ${EHovers.DARK1};
                    }
                    ${S_Content} {
                        box-shadow: ${EHovers.LIGHT4};
                        cursor: pointer;
                    }
                }
            `;
        }
    }}
`;

const S_SidebarSectionHeader = styled(SidebarSectionHeader)<{ $isUpdatingIndex: boolean }>`
    cursor: ${(props) => (props.$isUpdatingIndex ? 'unset' : 'pointer')};
`;

const S_TextValuePreview = styled(TextValuePreview)``;

const S_Content = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
`;

const S_TextValue2 = styled(TextValue2)``;

const S_GradientLine = styled(GradientLine)`
    margin: 15px 0 0 0;
`;
